import React, {useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import moment from 'moment';
import AppText from '../../../../../../common/AppText';
import CustomSwitch from '../../../../../../common/CustomSwitch';
import SimpleInput from '../../../../../../common/SimpleInput';
import DropDownMenu from '../../../../../../common/DropDownMenu';
import InfoTooltip from '../../../../../../common/InfoTooltip';
import CustomButtonV2 from '../../../../../../common/CustomButtonV2';
import SharedTimepicker from '../../../../../../common/SharedTimepicker';
import AutoMessages from '../Notifications/AutoMessages';
import VStack from '../../../../../../common/Layout/VStack';
import HStack from '../../../../../../common/Layout/HStack';
import {GlobalStyleAttributes} from '../../../../../../../styles/GlobalStyles';
import {Colors} from '../../../../../../../styles/Colors';
import {t} from '../../../../../../../services/i18n';
import {dbTimeFormat, isMobile} from '../../../../../../../services/Helpers';
import {APPOINTMENT_WAITLIST_SETTINGS} from '../../../../../../../services/ConstHelper';
import {fetchApi} from '../../../../../../../services/HTTP';
import {toggleScheduleLoader, toggleSuccessFlag} from '../../../../../../../redux/ScheduleSlice';
import {updateBox} from '../../../../../../../redux/boxSlice';
import {store} from '../../../../../../../redux/store';
import {autoMessageTypes} from "../../SettingsConfig";

const MAX_RANGES = 3;
const DEFAULT_RANGE = {from: '09:00:00', to: '18:00:00'};

const AppointmentWaitlistSettings = (props) => {
    const {values, setFieldValue, errors, validateForm, touched} = props;
    const shouldSave = useSelector((state) => state.schedule.settingsSaveFlag);
    const autoActions = useSelector((state) => state.box.box.auto_actions);
    const dispatch = useDispatch();
    const settings = values.appointmentWaitlistSettings || {};
    const ranges = settings.business_hours?.length ? settings.business_hours : [DEFAULT_RANGE];
    const rangeErrors = errors?.appointmentWaitlistSettings?.business_hours;
    const mobile = isMobile;
    const fontSize = GlobalStyleAttributes.fonts.standard16;
    const enabled = !!settings.enabled;

    const unitOptions = useMemo(() => ([
        {id: 'minutes', name: t('appointment-waitlist-minutes')},
        {id: 'hours', name: t('appointment-waitlist-hours')},
    ]), []);

    useEffect(() => {
        if (shouldSave) {
            saveMobile();
        }
    }, [shouldSave]);

    useEffect(() => {
        if (enabled) validateForm();
    }, [autoActions]);

    const saveMobile = async () => {
        if (!mobile) return;
        try {
            dispatch(toggleScheduleLoader(true));
            const setting = {prop_name: APPOINTMENT_WAITLIST_SETTINGS, properties: settings};
            await fetchApi('boxes/settings', 'POST', setting, false, true);
            dispatch(toggleScheduleLoader(false));
            dispatch(toggleSuccessFlag(true));
            dispatch(updateBox({
                settings: {
                    ...store.getState().box.box.settings,
                    [APPOINTMENT_WAITLIST_SETTINGS]: settings,
                },
            }));
        } catch (error) {
            console.log('error', error);
            dispatch(toggleScheduleLoader(false));
        }
    };

    return (
        <VStack space={'1.2rem'}>
            <VStack space={'0.5rem'}>
                <HStack justifyContent={'space-between'} alignItems={'center'}>
                    <HStack alignItems={'center'}>
                        <AppText fontSize={fontSize} boldFont>{t(`appointment-waitlist-settings-header${mobile ? '-mobile' : ''}`)}</AppText>
                        <InfoTooltip title={t('appointment-waitlist-settings-tooltip')}/>
                    </HStack>
                    <CustomSwitch isChecked={enabled} onToggle={(val) => setFieldValue('appointmentWaitlistSettings.enabled', !!val)}/>
                </HStack>
                <AppText fontSize={fontSize}>
                    {t(`appointment-waitlist-settings-explanation${mobile ? '-mobile' : ''}`)}
                    {' '}
                    <span onClick={() => window.open(t('help-center-appointment-waitlist'), '_blank')} style={{color: Colors.newDesign.mainBlue, cursor: 'pointer'}}>
                        {t('appointment-waitlist-help-center')}
                    </span>
                </AppText>
            </VStack>

            {enabled && (
                <VStack space={'1.2rem'}>
                    <VStack space={'0.5rem'}>
                        <AppText fontSize={fontSize} mediumFont>{t('appointment-waitlist-time-setting')}</AppText>
                        <HStack space={'0.5rem'} alignItems={'center'} style={{flexFlow: 'wrap', rowGap: '0.4rem'}}>
                            <AppText fontSize={fontSize}>{t('appointment-waitlist-confirm-before')}</AppText>
                            <SimpleInput
                                value={settings.confirm_time}
                                onChangeCallback={(val) => setFieldValue('appointmentWaitlistSettings.confirm_time', val === '' ? '' : parseInt(val, 10))}
                                width={'4rem'}
                                numericOnly={true}
                                styleOverride={{height: '30px'}}
                                status={errors?.appointmentWaitlistSettings?.confirm_time ? 'error' : null}
                            />
                            <DropDownMenu
                                title={unitOptions.find(u => u.id === settings.confirm_unit)?.name || t('appointment-waitlist-minutes')}
                                items={unitOptions}
                                onPress={(val) => setFieldValue('appointmentWaitlistSettings.confirm_unit', val)}
                                fontSize={fontSize}
                                width={'7rem'}
                                showBorder={true}
                                styleOverride={{height: '28px'}}
                            />
                            <HStack alignItems={'center'}>
                                <AppText fontSize={fontSize}>{t('appointment-waitlist-confirm-after')}</AppText>
                                <InfoTooltip title={t('appointment-waitlist-confirm-tooltip')}/>
                            </HStack>
                        </HStack>
                        {errors?.appointmentWaitlistSettings?.confirm_time && <AppText fontSize={fontSize} color={Colors.error} mediumFont>{errors.appointmentWaitlistSettings.confirm_time}</AppText>}
                    </VStack>

                    <VStack space={'0.5rem'}>
                        <AutoMessages
                            messageType={autoMessageTypes.APPOINTMENT_WAITLIST_OFFER}
                            sessionType={'appointment'}
                            header={t('appointment-waitlist-edit-template')}
                            subheader={t('appointment-waitlist-template-subheader')}
                            sectionHeader={{title: t('appointment-waitlist-message-template')}}
                            mediumHeader={true}
                        />
                        {errors?.appointmentWaitlistSettings?.offerTemplate && <AppText fontSize={fontSize} color={Colors.error}>{errors.appointmentWaitlistSettings.offerTemplate}</AppText>}
                    </VStack>

                    <VStack space={'0.5rem'}>
                        <HStack alignItems={'center'}>
                            <AppText fontSize={fontSize} mediumFont>{t('appointment-waitlist-offer-hours')}</AppText>
                            <InfoTooltip title={t('appointment-waitlist-offer-hours-tooltip')}/>
                        </HStack>
                        <AppText fontSize={fontSize}>{t('appointment-waitlist-offer-hours-helper')}</AppText>
                        <HStack alignItems={'center'} space={'0.5rem'}>
                            {settings.offer_hours?.from && (
                                <VStack style={{width: '110px'}}>
                                    <SharedTimepicker
                                        type={'time'}
                                        value={moment(settings.offer_hours.from, dbTimeFormat)}
                                        onChange={(e) => setFieldValue('appointmentWaitlistSettings.offer_hours.from', e.format(dbTimeFormat))}
                                        stepMinute={15}
                                        isError={!!errors?.appointmentWaitlistSettings?.offer_hours?.from}
                                        inputStyle={{alignItems: 'center', height: '30px'}}
                                    />
                                </VStack>
                            )}
                            <AppText fontSize={fontSize}>-</AppText>
                            {settings.offer_hours?.to && (
                                <VStack style={{width: '110px'}}>
                                    <SharedTimepicker
                                        type={'time'}
                                        value={moment(settings.offer_hours.to, dbTimeFormat)}
                                        onChange={(e) => setFieldValue('appointmentWaitlistSettings.offer_hours.to', e.format(dbTimeFormat))}
                                        stepMinute={15}
                                        isError={!!errors?.appointmentWaitlistSettings?.offer_hours?.to}
                                        inputStyle={{alignItems: 'center', height: '30px'}}
                                    />
                                </VStack>
                            )}
                        </HStack>
                        {settings.offer_hours?.from === '00:00:00' && settings.offer_hours?.to === '00:00:00' && <AppText fontSize={GlobalStyleAttributes.fonts.heading4} color={Colors.newDesign.darkGrey}>{t('appointment-waitlist-offer-hours-all-day')}</AppText>}
                        {(errors?.appointmentWaitlistSettings?.offer_hours?.from || errors?.appointmentWaitlistSettings?.offer_hours?.to) && (
                            <AppText fontSize={fontSize} color={Colors.error} mediumFont>
                                {errors.appointmentWaitlistSettings.offer_hours.from || errors.appointmentWaitlistSettings.offer_hours.to}
                            </AppText>
                        )}
                    </VStack>

                    <VStack space={'0.5rem'}>
                        <HStack alignItems={'center'}>
                            <AppText fontSize={fontSize} mediumFont>{t('appointment-waitlist-business-hours')}</AppText>
                            <InfoTooltip title={t('appointment-waitlist-business-hours-tooltip')}/>
                        </HStack>
                        <AppText fontSize={fontSize}>{t('appointment-waitlist-business-hours-helper')}</AppText>
                        <VStack space={'0.6rem'}>
                            {ranges.map((range, index) => {
                                const rowError = Array.isArray(rangeErrors) ? rangeErrors[index] : null;
                                const rowErrorMessage = typeof rowError === 'string'
                                    ? rowError
                                    : (rowError?.from || rowError?.to || null);
                                return (
                                    <VStack key={`waitlist-hours-${index}`} space={'0.2rem'}>
                                        <HStack alignItems={'center'} space={'0.5rem'}>
                                            {range.from && (
                                                <VStack style={{width: '110px'}}>
                                                    <SharedTimepicker
                                                        type={'time'}
                                                        value={moment(range.from, dbTimeFormat)}
                                                        onChange={(e) => setFieldValue(`appointmentWaitlistSettings.business_hours[${index}].from`, e.format(dbTimeFormat))}
                                                        stepMinute={15}
                                                        isError={!!rowError?.from}
                                                        inputStyle={{alignItems: 'center', height: '30px'}}
                                                    />
                                                </VStack>
                                            )}
                                            <AppText fontSize={fontSize}>-</AppText>
                                            {range.to && (
                                                <VStack style={{width: '110px'}}>
                                                    <SharedTimepicker
                                                        type={'time'}
                                                        value={moment(range.to, dbTimeFormat)}
                                                        onChange={(e) => setFieldValue(`appointmentWaitlistSettings.business_hours[${index}].to`, e.format(dbTimeFormat))}
                                                        stepMinute={15}
                                                        isError={!!rowError?.to}
                                                        inputStyle={{alignItems: 'center', height: '30px'}}
                                                    />
                                                </VStack>
                                            )}
                                            {index > 0 && (
                                                <CustomButtonV2
                                                    type={'link'}
                                                    iconName={'fa-trash'}
                                                    onClick={() => setFieldValue('appointmentWaitlistSettings.business_hours', ranges.filter((_, i) => i !== index))}
                                                />
                                            )}
                                        </HStack>
                                        {rowErrorMessage && <AppText fontSize={fontSize} color={Colors.error}>{rowErrorMessage}</AppText>}
                                    </VStack>
                                );
                            })}
                        </VStack>
                        {typeof rangeErrors === 'string' && touched?.appointmentWaitlistSettings?.business_hours && <AppText fontSize={fontSize} color={Colors.error} mediumFont>{rangeErrors}</AppText>}
                        {ranges.length < MAX_RANGES && (
                            <CustomButtonV2
                                onClick={() => setFieldValue('appointmentWaitlistSettings.business_hours', [...ranges, {...DEFAULT_RANGE}])}
                                text={t('appointment-waitlist-add-time-range')}
                                applyHoverOnText={true}
                                type={'link'}
                                iconProps={{iconColor: Colors.newDesign.mainBlue, iconHoverColor: Colors.newDesign.mainBlueHover}}
                                wrapperStyle={{width: 'max-content'}}
                            />
                        )}
                    </VStack>
                </VStack>
            )}
        </VStack>
    );
};

export default AppointmentWaitlistSettings;
